import {
  ApolloError,
  isApolloError,
  MutationHookOptions as ApolloMutationHookOptions,
  MutationResult,
  OperationVariables,
  useMutation as useApolloMutation,
} from '@apollo/client'
import * as React from 'react'

import { useApolloDefenderCtx } from '../../errors/ApolloDefender'
import { ApolloOperationError } from '../../errors/helpers/ApolloOperationError'
import { captureWithSentry } from '../../sentry/captureWithSentry'

type TMutationDocument = FirstParam<typeof useApolloMutation>

type TOptions = {
  /**
   * determines whether an error will be handled via custom error handling system,
   * does not affect sentry reporting
   *
   * ! Use this option instead of Apollo's build-in `errorPolicy`.
   * We decided not to rely on it as it has changed several times in the past and may not be stable yet,
   * and its behavior is also poorly documented.
   *
   * see {@link https://github.com/apollographql/apollo-client/issues/6831#issuecomment-808600284} for additional information
   *
   * ! all errors (including those that are not handled automatically) will still be returned so they can be handled manually
   *
   *  - `all`: all ApolloErrors handled, _default_
   *  - `major`: `graphQLErrors` are omitted to be handled manually, other ApolloErrors are handled
   *  - `off`: no errors are handled automatically
   */
  errorHandling?: 'all' | 'major' | 'off'
}

// reexport because code-gen takes the type from the same file as the hook
export type MutationHookOptions<TData, TVariables> = ApolloMutationHookOptions<TData, TVariables> &
  TOptions

/**
 * Apollo Client’s `useMutation` wrapper with custom error handling
 *
 * ! We use the custom `errorHandling` option (see {@link TOptions}) instead of Apollo's build-in `errorPolicy`.
 */
export const useMutation = <TData, TVariables extends OperationVariables = OperationVariables>(
  mutation: TMutationDocument,
  { errorHandling = 'all', ...hookOptions }: MutationHookOptions<TData, TVariables>,
) => {
  const [mutate, originalResult] = useApolloMutation<TData, TVariables>(mutation, hookOptions)

  const [result, setResult] = React.useState<MutationResult<TData>>(originalResult)

  const { reportError, handleError } = useApolloDefenderCtx()

  // add automatic error handling on each M call
  const mutateWithErrorHandle = React.useCallback(
    (options?: FirstParam<typeof mutate>) => {
      return mutate(options)
        .then((result) => {
          setResult((prev) => {
            return { ...prev, data: result.data }
          })

          return {
            ...result,
            isErrorHandled: false,
          }
        })
        .catch((error) => {
          const isError = error instanceof Error

          if (!isError) {
            captureWithSentry({
              error: `unknown error occurred while executing a mutation`,
              extras: { error },
            })

            setResult((prev) => {
              return {
                ...prev,
                errors: undefined,
                data: undefined,
              }
            })

            return {
              errors: undefined,
              data: undefined,
              isErrorHandled: true,
            }
          }

          const extendedError =
            error instanceof ApolloError
              ? new ApolloOperationError(
                  error,
                  mutation,
                  options?.variables ?? hookOptions.variables,
                )
              : error

          // always report the error to sentry
          reportError(extendedError)

          const shouldHandle = shouldHandleAutomatically(errorHandling)(error)

          // if it should be handled automatically, call the error handling mechanism
          if (shouldHandle) {
            handleError(extendedError)
          }

          setResult((prev) => {
            return {
              ...prev,
              data: undefined,
              errors: getErrors(error),
            }
          })

          return {
            data: undefined,
            errors: getErrors(error),
            isErrorHandled: shouldHandle,
          }
        })
    },
    [mutate, mutation, reportError, handleError, errorHandling, hookOptions.variables],
  )

  const resultWithLoading = { ...result, loading: originalResult.loading }

  return [mutateWithErrorHandle, resultWithLoading] as const
}

// * HELPERS

// parses errors from obtained error object to return them from the mutate function
const getErrors = (error: Error) => {
  if (!isApolloError(error)) {
    return error
  }

  const { graphQLErrors, networkError } = error

  if (graphQLErrors.length > 0) {
    return graphQLErrors
  }

  return networkError ?? error
}

/** decides if the ApolloError should be handled via custom `onError` function from the `ApolloDefender`,
 * it depends on the `errorHandling` option passed into the generated mutation hook
 *
 * see {@link TOptions} for more details about the `errorHandling` option
 */
const shouldHandleAutomatically =
  (errorHandling: TOptions['errorHandling']) =>
  (error: Error): error is ApolloError => {
    if (!isApolloError(error)) {
      return false
    }

    const isNotGraphQLErrors = error.graphQLErrors.length === 0

    switch (errorHandling) {
      case `all`:
        return true
      case `major`:
        return isNotGraphQLErrors
      case `off`:
      default:
        return false
    }
  }
